// src/auth/effectiveSession.js
// Central effective-session helper for Web, Android and iOS.
//
// Goal:
// - App/Header/ProtectedRoute should not each invent their own auth truth.
// - Web/Android prefer Firebase Auth.
// - iOS Capacitor can use manualAuth as the effective session because WKWebView
//   can delay Firebase Web Auth hydration.
//
// This file is intentionally pure: no React hooks, no navigation, no Firestore reads.

import { isIosCapacitor } from "../utils/nativeHttp";
import { getEffectiveManualAuthSession } from "../utils/manualAuth";

function clean(value) {
  return String(value || "").trim();
}

function firebaseUserToSession(firebaseUser, userInfo = {}) {
  if (!firebaseUser) return null;
  const uid = clean(firebaseUser.uid);
  const email = clean(firebaseUser.email || userInfo.email);
  const displayName = clean(userInfo.nombre || userInfo.displayName || userInfo.name || firebaseUser.displayName || "");
  if (!uid && !email) return null;

  return {
    loggedIn: true,
    source: "firebase-web",
    sessionKey: uid ? `uid:${uid}` : `email:${email.toLowerCase()}`,
    user: {
      uid: uid || null,
      email,
      displayName,
    },
    headerUserInfo: {
      nombre: displayName,
      email,
    },
    firebaseUser,
    manualAuth: null,
  };
}

function manualSessionToEffective(manualSession, userInfo = {}) {
  if (!manualSession || !manualSession.loggedIn || !manualSession.user) return null;

  const uid = clean(manualSession.user.uid);
  const email = clean(manualSession.user.email || userInfo.email);
  const displayName = clean(
    userInfo.nombre ||
    userInfo.displayName ||
    userInfo.name ||
    manualSession.user.displayName ||
    manualSession.manualAuth?.displayName ||
    manualSession.manualAuth?.name ||
    ""
  );

  if (!uid && !email) return null;

  return {
    loggedIn: true,
    source: manualSession.source || "ios-manual",
    sessionKey: uid ? `uid:${uid}` : `email:${email.toLowerCase()}`,
    user: {
      uid: uid || null,
      email,
      displayName,
    },
    headerUserInfo: {
      nombre: displayName,
      email,
    },
    firebaseUser: null,
    manualAuth: manualSession.manualAuth || null,
  };
}

export function buildEffectiveSession({
  firebaseUser = null,
  userInfo = {},
  isIosRuntime = null,
} = {}) {
  const ios = isIosRuntime === null ? isIosCapacitor() : Boolean(isIosRuntime);

  // iOS native app: manualAuth is the trusted immediate session because it
  // contains the token used by REST Firestore and updates instantly after login.
  if (ios) {
    const manualEffective = manualSessionToEffective(getEffectiveManualAuthSession(), userInfo);
    if (manualEffective) return manualEffective;

    // Fallback only: if Firebase Auth happens to hydrate on iOS, allow it.
    const firebaseEffective = firebaseUserToSession(firebaseUser, userInfo);
    if (firebaseEffective) return { ...firebaseEffective, source: "firebase-web-ios-fallback" };
  }

  // Web / Android: Firebase Web Auth remains the primary source.
  const firebaseEffective = firebaseUserToSession(firebaseUser, userInfo);
  if (firebaseEffective) return firebaseEffective;

  return {
    loggedIn: false,
    source: "guest",
    sessionKey: "guest",
    user: null,
    headerUserInfo: { nombre: "", email: "" },
    firebaseUser: null,
    manualAuth: null,
  };
}

export function getCurrentEffectiveSession({ firebaseAuth = null, userInfo = {}, isIosRuntime = null } = {}) {
  const firebaseUser = firebaseAuth && firebaseAuth.currentUser ? firebaseAuth.currentUser : null;
  return buildEffectiveSession({ firebaseUser, userInfo, isIosRuntime });
}

export function getEffectiveSessionKey(session) {
  return session && session.sessionKey ? session.sessionKey : "guest";
}

export function isEffectiveSessionLoggedIn(session) {
  return Boolean(session && session.loggedIn && session.user && (session.user.uid || session.user.email));
}
